import React, { useEffect } from "react";
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  CircularProgress,
  useTheme,
  Fade,
  Grow,
  Slide,
} from "@mui/material";
import {
  Close as CloseIcon,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { ScannerState } from "../hooks/useDriverScanner";
import { useScannerSounds } from "@/hooks/useScannerSounds";
import BarcodeScanner from "@/components/thirdParty/react-barcode-reader";

interface DriverScannerModalProps {
  state: ScannerState;
  timeoutSeconds: number;
  onClose: () => void;
  onReset: () => void;
  onScan: (scannedValue: string) => void;
}

const CountdownTimer = ({ seconds, total }: { seconds: number; total: number }) => {
  const theme = useTheme();
  const progress = (seconds / total) * 100;
  const isUrgent = seconds <= 10;
  const radius = 70;
  const circumference = 2 * Math.PI * radius;
  const offset = -((100 - progress) / 100) * circumference;

  const timerColor = isUrgent ? theme.palette.error.main : theme.palette.primary.main;

  return (
    <Box sx={{ position: "relative", display: "inline-flex" }}>
      <svg width="180" height="180" style={{ transform: "rotate(-90deg)" }}>
        <circle
          cx="90"
          cy="90"
          r={radius}
          fill="none"
          stroke={theme.palette.action.disabledBackground}
          strokeWidth="8"
        />
        <circle
          cx="90"
          cy="90"
          r={radius}
          fill="none"
          stroke={timerColor}
          strokeWidth="8"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          style={{
            transition: "stroke 0.3s ease, stroke-dashoffset 1s linear",
          }}
        />
      </svg>
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
        }}
      >
        <Typography
          variant="h2"
          sx={{
            fontWeight: 600,
            fontSize: "3rem",
            color: timerColor,
            lineHeight: 1,
            fontVariantNumeric: "tabular-nums",
            transition: "color 0.3s ease",
          }}
        >
          {seconds}
        </Typography>
      </Box>
    </Box>
  );
};

const DriverScannerModal: React.FC<DriverScannerModalProps> = ({
  state,
  timeoutSeconds,
  onClose,
  onReset,
  onScan,
}) => {
  const { t } = useTranslation();
  const { playSuccess, playError } = useScannerSounds();
  const isOpen = state.status !== "idle" && state.status !== "error" && state.status !== "success";

  useEffect(() => {
    if (state.status === "success") {
      playSuccess();
    } else if (state.status === "error") {
      playError();
    }
  }, [state.status, playSuccess, playError]);

  return (
    <>
      {isOpen && (
        <BarcodeScanner
          onScan={onScan}
          onError={() => { }}
          minLength={1}
        />
      )}

      <Dialog
        open={isOpen}
        onClose={() => {
          if (state.status === "waiting") {
            onClose();
          }
        }}
        maxWidth="sm"
        fullWidth
        slotProps={{
          transition: { direction: "up" },
          paper: {
            sx: {
              borderRadius: 2,
              padding: 3,
              minHeight: 280,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              position: "relative",
              overflow: "hidden"
            },
          },
        }}
        sx={{
          zIndex: 1300,
        }}
        slots={{
          transition: Slide,
        }}
      >
        <DialogContent sx={{ textAlign: "center", padding: 4 }}>
          {state.status === "validating" && (
            <Box>
              <Box
                sx={{
                  animation: "pulse 2s ease-in-out infinite",
                  "@keyframes pulse": {
                    "0%, 100%": { transform: "scale(1)", opacity: 1 },
                    "50%": { transform: "scale(1.05)", opacity: 0.9 },
                  },
                }}
              >
                <CircularProgress size={80} thickness={4} />
              </Box>
              <Typography variant="h6" sx={{ marginTop: 3, fontWeight: 500 }}>
                {t("driverShiftTracker.scanner.processing")}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ marginTop: 1 }}>
                {t("driverShiftTracker.scanner.validatingStaffPass")}
              </Typography>
            </Box>
          )}

          {state.status === "waiting" && (
            <Fade in timeout={400}>
              <Box>
                <Grow in timeout={500}>
                  <Typography
                    variant="h4"
                    sx={{
                      marginBottom: 2,
                      fontWeight: 600,
                    }}
                  >
                    {state.user.name}
                  </Typography>
                </Grow>

                <Typography
                  variant="h6"
                  color="text.secondary"
                  sx={{ marginBottom: 3, fontWeight: 400 }}
                >
                  {t("driverShiftTracker.scanner.waitingForAction")}
                </Typography>

                <CountdownTimer seconds={timeoutSeconds} total={30} />

                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ marginTop: 3, display: "block" }}
                >
                  {t("driverShiftTracker.scanner.clickToCancel")}
                </Typography>
              </Box>
            </Fade>
          )}

          {state.status === "processing" && (
            <Box>
              <CircularProgress size={80} thickness={4} />
              <Typography variant="h6" sx={{ marginTop: 3, fontWeight: 500 }}>
                {t("driverShiftTracker.scanner.processing")}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ marginTop: 1 }}>
                {t("driverShiftTracker.scanner.updatingShiftStatus")}
              </Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {state.status === "error" && state.message && (
        <Dialog
          open={true}
          onClose={onReset}
          maxWidth="sm"
          fullWidth
          slotProps={{
            paper: {
              sx: {
                borderRadius: 2,
                padding: 3,
                zIndex: 8888,
              },
            },
          }}
        >
          <DialogContent sx={{ textAlign: "center", paddingY: 4 }}>
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: "50%",
                backgroundColor: "error.main",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto",
                marginBottom: 2,
              }}
            >
              <CloseIcon sx={{ fontSize: 50, color: "#ffffff" }} />
            </Box>

            <Typography
              variant="h5"
              sx={{
                marginBottom: 2,
                fontWeight: "bold"
              }}
            >
              {t("driverShiftTracker.scanner.scanError")}
            </Typography>

            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ fontWeight: 500 }}
            >
              {state.message}
            </Typography>

            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ marginTop: 3, display: "block" }}
            >
              {t("driverShiftTracker.scanner.pleaseTryAgain")}
            </Typography>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};

export default DriverScannerModal;
